import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, Monitor, ChevronDown, LogOut, User, Settings } from "lucide-react";
import logoImg from "@/assets/logo.jpg";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { useLocale, localeOptions, type SiteLocale, type SiteTranslations } from "@/contexts/LocaleContext";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2">
    <img src={logoImg} alt="DocSeal" className="w-8 h-8 rounded-lg object-contain" />
    <span className="font-display font-bold text-lg tracking-tight">
      Doc<span className="text-gradient-gold">Seal</span>
    </span>
  </Link>
);

const ThemeSwitcher = () => {
  const { theme, setTheme } = useTheme();
  const modes = [
    { key: "light", icon: Sun },
    { key: "dark", icon: Moon },
    { key: "system", icon: Monitor },
  ] as const;
  return (
    <div className="hidden md:flex items-center gap-0.5 rounded-full border border-border bg-card/60 p-0.5">
      {modes.map((m) => (
        <button
          key={m.key}
          onClick={() => setTheme(m.key)}
          className={`p-1.5 rounded-full transition-colors ${
            theme === m.key
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          title={m.key}
        >
          <m.icon className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
};

const LanguageSwitcher = () => {
  const { locale, setLocale } = useLocale();
  const current = localeOptions.find((lo) => lo.key === locale);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 px-2 py-1.5 rounded-full border border-border bg-card/60 text-sm hover:bg-accent/10 transition-colors">
          <span>{current?.flag}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        {localeOptions.map((lo) => (
          <DropdownMenuItem
            key={lo.key}
            onClick={() => setLocale(lo.key)}
            className={locale === lo.key ? "bg-accent text-accent-foreground" : ""}
          >
            <span className="mr-2">{lo.flag}</span>
            <span className="text-xs">{lo.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AboutDropdown = ({ label, isActive, t }: { label: string; isActive: boolean; t: SiteTranslations }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const subLinks = [
    { to: "/about", label: t.nav.aboutMenu.about },
    { to: "/contact", label: t.nav.aboutMenu.contact },
    { to: "/guide", label: t.nav.aboutMenu.guide },
    { to: "/faq", label: t.nav.aboutMenu.faq },
    { to: "/privacy", label: t.nav.aboutMenu.privacy },
    { to: "/terms", label: t.nav.aboutMenu.terms },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`transition-colors flex items-center gap-1 ${
          isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-36 rounded-lg border border-border bg-card shadow-lg py-1 z-50">
          {subLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const UserMenu = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{ display_name: string | null; avatar_url: string | null }>({ display_name: null, avatar_url: null });

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("display_name, avatar_url").eq("id", user.id).single().then(({ data }) => {
      if (data) setProfile(data);
    });
  }, [user]);

  if (!user) return null;

  const initials = (profile.display_name || user.email || "U").slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full ring-2 ring-border hover:ring-accent transition-all">
          <Avatar className="w-8 h-8">
            {profile.avatar_url && <AvatarImage src={profile.avatar_url} alt="avatar" />}
            <AvatarFallback className="text-xs bg-accent text-accent-foreground">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium truncate">{profile.display_name || user.email}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/dashboard")}>
          <User className="w-4 h-4 mr-2" />控制台
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/dashboard?tab=settings")}>
          <Settings className="w-4 h-4 mr-2" />设置
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { signOut(); navigate("/"); }}>
          <LogOut className="w-4 h-4 mr-2" />退出登录
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Navbar = () => {
  const { t } = useLocale();
  const { user } = useAuth();
  const location = useLocation();

  const aboutPaths = ["/about", "/contact", "/privacy", "/terms", "/guide", "/faq"];
  const isAboutActive = aboutPaths.includes(location.pathname);

  const navLinks = [
    { to: "/", label: t.nav.home },
    { to: "/features", label: t.nav.features },
    { to: "/technology", label: t.nav.technology },
    { to: "/cases", label: t.nav.cases },
    { to: "/pricing", label: t.nav.pricing },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="container flex items-center justify-between h-16">
        <div className="flex items-center gap-8">
          <Logo />
          <div className="hidden md:flex items-center gap-6 text-sm font-medium">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`transition-colors flex items-center gap-1 ${
                  location.pathname === link.to
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <AboutDropdown label={t.nav.about} isActive={isAboutActive} t={t} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ThemeSwitcher />
          <LanguageSwitcher />
          {user ? (
            <UserMenu />
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/auth">{t.nav.login}</Link>
              </Button>
              <Button variant="nav-cta" size="sm" asChild>
                <Link to="/auth">{t.nav.getStarted}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
